import 'isomorphic-fetch'

import { pageEndpoint, venuesEndpoint } from '~apiConfig'
import { getApiResponseInJSON, getIdToken } from '~utils'
import {
  ADD_NEW_PAGE,
  FETCH_PAGE_CONTENTS,
  INITIALIZE_PAGES,
  PAGE_UPDATE_STATUS,
  VENUE_EVENTS,
  STORE_PAGE_TO_EDIT,
  REMOVE_PAGE_BY_ID
} from '../actionTypes'

export const resetPageActionStatus = () => async dispatch => {
  dispatch({
    type: ADD_NEW_PAGE,
    payload: {
      error: false,
      message: '',
      success: false
    }
  })
}

export const resetUpdatePageStatus = () => async dispatch => {
  dispatch({
    type: PAGE_UPDATE_STATUS,
    payload: {
      message: '',
      success: false
    }
  })
}

export const fetchPagesList = params => async dispatch => {
  const { method, url } = pageEndpoint.list
  const idToken = getIdToken()
  const apiResponse = await fetch(url, {
    body: JSON.stringify(params),
    headers: {
      Authorization: `Bearer ${idToken}`
    },
    method
  })
    .then(async response => await getApiResponseInJSON(response))
    .catch(error => console.error(error))
  const { pages: rows, pagesCount: rowsCount } = apiResponse
  dispatch({
    type: FETCH_PAGE_CONTENTS,
    payload: {
      rows,
      rowsCount,
      params
    }
  })
}

/**
 * Returns true if a page with the given slug already exists in the database.
 * Does not dispatch anything — purely a read/check.
 */
export const checkPageSlugExists = async (slug, excludePageId) => {
  if (!slug) {
    return false
  }

  try {
    const { method, url } = pageEndpoint.list
    const idToken = getIdToken()
    const apiResponse = await fetch(url, {
      body: JSON.stringify({
        slug,
        excludePageId,
        pagination: { current: 1, pageSize: 1 }
      }),
      headers: { Authorization: `Bearer ${idToken}` },
      method
    })
      .then(async response => await getApiResponseInJSON(response))
      .catch(() => null)
    return apiResponse?.pagesCount > 0
  } catch {
    return false
  }
}

export const addNewPage = (page, accessToken) => async dispatch => {
  try {
    const { method, url } = pageEndpoint.add
    const idToken = getIdToken()
    await fetch(url, {
      body: JSON.stringify({ page }),
      headers: {
        Authorization: `Bearer ${idToken}`,
        AccessToken: accessToken,
        'Content-Type': 'application/json'
      },
      method
    })
      .then(async response => await getApiResponseInJSON(response))
      .then(response => {
        const { addedPage: { _id: newPageId }, message, success } = response
        dispatch({
          type: ADD_NEW_PAGE,
          payload: { newPageId, message, success }
        })
      })
      .catch((error) => {
        console.log(error)
        dispatch({
          type: ADD_NEW_PAGE,
          payload: { message: error.message, success: false }
        })
      })
  } catch (error) {
    console.log(error)
  }
}

export const fetchPageById = pageObjectId => async dispatch => {
  try {
    const { method, url } = pageEndpoint.edit(pageObjectId)
    const idToken = getIdToken()
    const apiResponse = await fetch(url, {
      headers: {
        Authorization: `Bearer ${idToken}`
      },
      method: method
    })
      .then(async response => await getApiResponseInJSON(response))
      .catch(error => console.error(error))
    dispatch({
      type: STORE_PAGE_TO_EDIT,
      payload: {
        ...apiResponse
      }
    })
  } catch (error) {
    console.log(error)
  }
}

export const updatePage = (pageObjectId, page, accessToken) => async dispatch => {
  try {
    const { method, url } = pageEndpoint.update(pageObjectId)
    const idToken = getIdToken()
    await fetch(url, {
      body: JSON.stringify(page),
      headers: {
        Authorization: `Bearer ${idToken}`,
        AccessToken: accessToken,
        'Content-Type': 'application/json'
      },
      method: method
    })
      .then(async response => await getApiResponseInJSON(response))
      .then(response => {
        const { message, success } = response
        dispatch({
          type: PAGE_UPDATE_STATUS,
          payload: { message, success }
        })
      })
      .catch((error) => {
        console.log(error)
        dispatch({
          type: PAGE_UPDATE_STATUS,
          payload: { message: error.message, success: false }
        })
      })
  } catch (error) {
    console.log(error)
  }
}

export const removePageById = (pageObjectId, accessToken) => async dispatch => {
  try {
    const { method, url } = pageEndpoint.delete(pageObjectId)
    const idToken = getIdToken()
    const apiResponse = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${idToken}`,
        AccessToken: accessToken
      }
    }).then(async response => await getApiResponseInJSON(response))

    const { message: deleteMessage, success } = apiResponse
    const deleteStatus = success !== false

    dispatch({
      type: REMOVE_PAGE_BY_ID,
      payload: {
        deleteMessage,
        deleteStatus
      }
    })

    return { ...apiResponse, success: deleteStatus }
  } catch (error) {
    const deleteMessage = error.message || 'Failed to delete page'
    dispatch({
      type: REMOVE_PAGE_BY_ID,
      payload: {
        deleteMessage,
        deleteStatus: false
      }
    })
    return { success: false, message: deleteMessage }
  }
}

export const initializePages = accessToken => async dispatch => {
  const { method, url } = pageEndpoint.new
  const idToken = getIdToken()
  const apiResponse = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      AccessToken: accessToken
    },
    method
  })
    .then(async response => await getApiResponseInJSON(response))
    .catch(error => console.error(error))
  const { allVenues, tourVenues } = apiResponse
  dispatch({
    type: INITIALIZE_PAGES,
    payload: {
      venues: allVenues,
      tourVenues
    }
  })
}

export const addEventToVlpHeroSlider = (eventId, venueId, accessToken) => async () => {
  const idToken = getIdToken()

  const findResponse = await fetch(pageEndpoint.findByVenue.url, {
    body: JSON.stringify({ type: 'venue-landing', venue_id: venueId }),
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    method: pageEndpoint.findByVenue.method
  }).then(async response => await getApiResponseInJSON(response))

  const page = findResponse?.page
  if (!page) {
    return { success: false, message: 'No Venue Landing Page found for this venue.' }
  }

  const pageId = page._id
  const existingCards = page?.hero?.custom_card || []

  const alreadyExists = existingCards.some(card => String(card.id) === String(eventId))
  if (alreadyExists) {
    return { success: false, message: 'This event is already in the VLP Hero Slider.' }
  }

  const updatedCards = [
    { id: eventId, instanceOf: 'Event' },
    ...existingCards
  ]

  const { method: updateMethod, url: updateUrl } = pageEndpoint.updateHeroCards(pageId)
  const updateResponse = await fetch(updateUrl, {
    body: JSON.stringify({ custom_card: updatedCards }),
    headers: {
      Authorization: `Bearer ${idToken}`,
      AccessToken: accessToken,
      'Content-Type': 'application/json'
    },
    method: updateMethod
  }).then(async response => await getApiResponseInJSON(response))

  return { success: updateResponse?.success !== false, message: updateResponse?.message || 'Event added to VLP Hero Slider.' }
}

export const fetchEventsVenueById = venueId => async dispatch => {
  const { method, url } = venuesEndpoint.events(venueId)
  const idToken = getIdToken()
  const apiResponse = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`
    },
    method
  })
    .then(async response => await getApiResponseInJSON(response))
    .catch(error => console.error(error))
  const { vlpHeroAllEvents, todaysEvent, upcomingEvents, recommendedEvents } = apiResponse
  dispatch({
    type: VENUE_EVENTS,
    payload: {
      todaysEvent,
      vlpHeroAllEvents,
      upcomingEvents,
      recommendedEvents
    }
  })
}
